import { storyblokEditable, StoryblokServerComponent, StoryblokServerRichText } from '@storyblok/react/rsc';
import { richTextResolvers } from '@/lib/richTextResolvers';
import PhosphorIcon from '@/components/PhosphorIcon';
import TooltipClient from '@/components/TooltipClient';
import { colorStyle, resolveLink } from '@/lib/helpers';

// Drop nodes without a `type` (orphan data from the old "check/x" schema would
// otherwise trip StoryblokServerRichText with "No resolver found for node type undefined").
const sanitizeNodes = (nodes) =>
  (nodes || [])
    .filter((n) => n && n.type)
    .map((n) => (Array.isArray(n.content) ? { ...n, content: sanitizeNodes(n.content) } : n));

// Flatten a richtext doc to plain text (to detect the "✓" check marker).
const plainText = (doc) => {
  if (typeof doc === 'string') return doc.trim();
  let t = '';
  (function walk(n) {
    if (Array.isArray(n)) return n.forEach(walk);
    if (n && typeof n === 'object') { if (n.text) t += n.text; if (n.content) walk(n.content); }
  })(doc?.content);
  return t.trim();
};

/**
 * A cell holds either nested bloks (an array, the current shape) or a legacy
 * rich text doc. Both are rendered so the content migration and the deploy don't
 * have to land in the same instant — a story either side of the migration renders
 * correctly on either side of the deploy. Once no rich text remains in the
 * column_a–column_d fields, everything below the array branch can go.
 */
const Cell = ({ value }) => {
  if (Array.isArray(value)) {
    if (value.length === 0) return null;
    return value.map((blok) => <StoryblokServerComponent blok={blok} key={blok._uid} />);
  }
  if (!value) return null;
  // A "✓" marker renders as the Check icon (16px, black); everything else as rich text.
  if (plainText(value) === '✓') return <PhosphorIcon name="Check" size={16} weight="bold" color="#251C29" />;
  if (typeof value === 'string') return <span>{value}</span>;
  const content = sanitizeNodes(value.content);
  if (content.length === 0) return null;
  return <StoryblokServerRichText document={{ ...value, content }} components={richTextResolvers} />;
};

/**
 * A row's feature name, with an optional link and info tooltip. Without a Link text
 * the whole name is the link; with one, that text follows the name as the link
 * ("…customers and non-customers", or "Get started…" after a section heading).
 */
const FeatureLabel = ({ row, linkClassName = '' }) => {
  const href = resolveLink(row.feature_link);
  const hasLink = !!href && href !== '#';
  const newTab = row.feature_link?.target === '_blank';
  const link = (text) => (
    <a
      href={href}
      target={newTab ? '_blank' : undefined}
      rel={newTab ? 'noopener noreferrer' : undefined}
      className={`underline text-magenta hover:text-magenta-50 ${linkClassName}`}
    >
      {text}
    </a>
  );

  return (
    <span>
      {hasLink && !row.link_label ? link(row.feature) : row.feature}
      {hasLink && row.link_label && <> {link(row.link_label)}</>}
      {row.feature_tooltip && (
        // A span, not a p: base.css sizes every p at 16–18px, over the bubble's 14px.
        // The 18px icon matches the old site's ph-lg info icon next to 14px text, and
        // the bubble opens rightwards, as the old site's did (data-position="right").
        <TooltipClient iconSize={18} align="start">
          <span className="whitespace-pre-line">{row.feature_tooltip}</span>
        </TooltipClient>
      )}
    </span>
  );
};

// Whether a cell has anything in it — drives whether columns C/D appear at all.
const hasCell = (v) => {
  if (Array.isArray(v)) return v.length > 0;
  if (typeof v === 'string') return !!v;
  return !!v?.content?.some?.((n) => n.content?.length);
};

// Fold each run of sub-rows under the row before it. A sub-row with nothing to fold
// under (first row, or right after a section heading) stays an ordinary row. Each
// row keeps its index in the full list, which the corner rounding reads.
const groupRows = (rows) => {
  const items = [];
  rows.forEach((row, idx) => {
    const prev = items[items.length - 1];
    if (row.sub_row && !row.section_heading && prev && !prev.row.section_heading) prev.subs.push({ row, idx });
    else items.push({ row, idx, subs: [] });
  });
  return items;
};

const GroupCaret = () => (
  <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 256 256" className="shrink-0 transition-transform group-open/sub:rotate-180" aria-hidden="true">
    <path fill="currentColor" d="M213.66 101.66 128 187.31 42.34 101.66a8 8 0 0 1 11.32-11.32L128 164.69l74.34-74.35a8 8 0 0 1 11.32 11.32Z" />
  </svg>
);

const ComparisonTable = ({ blok }) => {
  const rows = blok.rows || [];
  // 2 columns by default (a, b) — matches existing tables. c/d appear only when
  // given a label or any row has content for them, so 2-column tables are unchanged.
  const cols = [
    { key: 'column_a', label: blok.column_a_label || '', color: blok.column_a_color || '#D3C1F8', header: blok.header_background_a || blok.column_a_color || '#D3C1F8' },
    { key: 'column_b', label: blok.column_b_label || '', color: blok.column_b_color || '#F2F2F2', header: blok.header_background_b || blok.column_b_color || '#F2F2F2' },
  ];
  if (blok.column_c_label || rows.some((r) => hasCell(r.column_c)))
    cols.push({ key: 'column_c', label: blok.column_c_label || '', color: blok.column_c_color || '#F2F2F2', header: blok.header_background_c || blok.column_c_color || '#F2F2F2' });
  if (blok.column_d_label || rows.some((r) => hasCell(r.column_d)))
    cols.push({ key: 'column_d', label: blok.column_d_label || '', color: blok.column_d_color || '#F2F2F2', header: blok.header_background_d || blok.column_d_color || '#F2F2F2' });

  // The leading feature column earns its place only if some data row names a feature.
  // Section headings are excluded on purpose: they span the full width (gridColumn
  // '1 / -1') rather than sitting in the column, so a headings-only table would
  // otherwise keep an empty column forever.
  const hasFeatures = rows.some((r) => !r.section_heading && r.feature?.trim());

  const featureCol = blok.feature_column_background || '#FFFFFF';
  const featureTextCol = blok.feature_column_text_color || '';
  const columnGap = blok.column_gap || '2px';
  const rowGap = blok.row_gap || '0';
  // The pricing style copies the old site's plan comparison: a feature column at
  // least 400px wide, regular-weight feature names, large column headers and
  // small section headings.
  const isPricing = blok.table_style === 'pricing';
  const valueTracks = `repeat(${cols.length}, minmax(0,1fr))`;
  const trackVars = {
    '--cms-ct-mobile': valueTracks,
    '--cms-ct-desktop': isPricing && hasFeatures
      ? `minmax(25rem,2fr) ${valueTracks}`
      : `repeat(${cols.length + (hasFeatures ? 1 : 0)}, minmax(0,1fr))`,
    '--cms-ct-minwidth': isPricing && hasFeatures
      ? `${25 + cols.length * 8}rem`
      : cols.length > 2 ? '48rem' : '31.25rem',
    '--cms-ct-stickybg': blok.background_color || '#FFFFFF',
  };

  const dataRow = (row, idx, isGroupHeader = false) => {
    const isLast = idx === rows.length - 1;
    // A row of nothing but buttons (the closing "Get started" row) has no room on a
    // phone, four to a row, so it shows from md up only, as on the old site.
    const buttonsOnly = !row.feature?.trim() && cols.every((c) =>
      Array.isArray(row[c.key]) && row[c.key].length > 0 && row[c.key].every((b) => b.component === 'button'));
    return (
      <div key={row._uid} className={`cms-comparison-grid ${buttonsOnly ? 'cms-comparison-buttons' : ''}`} style={{ columnGap }}>
        {hasFeatures && (
          <div
            className={`cms-comparison-feature ${isLast ? 'rounded-bl-[0.5rem]' : ''} ${idx === 0 ? 'rounded-tl-[0.5rem]' : ''} p-4 ${isPricing ? 'text-14' : 'text-14 sm:text-16 font-bold'} ${isGroupHeader ? 'justify-between gap-2 group-open/sub:font-bold' : ''} flex items-center`}
            style={{
              // A row with no feature name (e.g. a closing row of buttons) leaves the column blank.
              backgroundColor: row.feature ? featureCol : 'transparent',
              ...(featureTextCol ? { color: featureTextCol } : {}),
            }}
          >
            <FeatureLabel row={row} />
            {isGroupHeader && <GroupCaret />}
          </div>
        )}
        {cols.map((c, i) => (
          <div
            key={c.key}
            className={`${isLast && i === cols.length - 1 ? 'rounded-br-[0.5rem]' : ''} ${!hasFeatures && isLast && i === 0 ? 'rounded-bl-[0.5rem]' : ''} p-4 text-center text-14 sm:text-16 flex items-center justify-center`}
            style={{ backgroundColor: c.color }}
          >
            <Cell value={row[c.key]} />
          </div>
        ))}
      </div>
    );
  };

  return (
    <div {...storyblokEditable(blok)} style={colorStyle(blok)}>
      <div className="cms-comparison-scroll">
          <div className="grid cms-comparison-body" style={{ ...trackVars, rowGap }}>
            {/* Header row — sticky on mobile, see app/styles/comparison-table.css */}
            <div className="cms-comparison-grid cms-comparison-head" style={{ columnGap }}>
              {hasFeatures && <div className="bg-transparent cms-comparison-corner" />}
              {cols.map((c, i) => (
                <div
                  key={c.key}
                  // Four plan names share a phone's width, so they lose the side padding there.
                  className={`px-1 py-4 md:p-4 text-center font-bold ${isPricing ? 'text-18 sm:text-20 md:text-24' : 'text-16 sm:text-18'} ${i === 0 ? 'rounded-tl-[0.5rem]' : ''} ${i === cols.length - 1 ? 'rounded-tr-[0.5rem]' : ''}`}
                  style={{ backgroundColor: c.header }}
                >
                  {c.label}
                </div>
              ))}
            </div>

            {/* Data rows. A sub-row folds under the row above it, which becomes
                an expandable group header (the old site's "extra rows"). */}
            {groupRows(rows).map(({ row, idx, subs }) => {
              if (row.section_heading) {
                // A section heading spans all columns as a full-width, background-less
                // label separating groups of features (e.g. "E-commerce accounting").
                return (
                  <div key={row._uid} className="cms-comparison-grid" style={{ columnGap }}>
                    <div
                      className={`p-4 pt-8 font-bold uppercase ${isPricing ? 'text-14' : 'text-16 sm:text-18'}`}
                      style={{ gridColumn: '1 / -1' }}
                    >
                      <FeatureLabel row={row} linkClassName="ml-1 font-normal normal-case" />
                    </div>
                  </div>
                );
              }
              if (subs.length === 0) return dataRow(row, idx);
              // Open, the group is framed by lavender lines above and below and its
              // header turns bold, as on the old site.
              return (
                <details key={row._uid} className="group/sub open:border-y-2 open:border-[#BFACEA]">
                  <summary className="list-none cursor-pointer [&::-webkit-details-marker]:hidden">
                    {dataRow(row, idx, true)}
                  </summary>
                  <div className="grid" style={{ rowGap, marginTop: rowGap }}>
                    {subs.map((sub) => dataRow(sub.row, sub.idx))}
                  </div>
                </details>
              );
            })}
          </div>
        </div>
    </div>
  );
};

export default ComparisonTable;
